import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, FileSpreadsheet, FileText, RefreshCw, Save, Sparkles } from 'lucide-react';
import { AccesRefuse, Button, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type TableauTemplate,
  downloadLiasse,
  dupliquerExercice,
  genererDepuisBalance,
  getExercice,
  getTemplates,
  listExercices,
  seedLiasse2023,
  setValeurs,
} from '@/services/liasse';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

type Edits = Record<string, Record<string, Record<string, string>>>; // tableau→cle→col→texte

/**
 * Liasse fiscale officielle (imprimé destiné à l'administration). FORME figée côté serveur
 * (libellés/ordre des tableaux), DONNÉES par exercice saisies ici. Génère le PDF/Excel fidèles.
 */
export function LiasseOfficiellePage() {
  const perms = usePerms('liasse');
  const qc = useQueryClient();
  const [selId, setSelId] = useState<string>('');
  const [tab, setTab] = useState('bilan_actif');
  const [edits, setEdits] = useState<Edits>({});

  const { data: templates } = useQuery({ queryKey: ['liasse-templates'], queryFn: getTemplates, enabled: perms.canConsulter });
  const { data: exercices, isLoading } = useQuery({ queryKey: ['liasse-exercices'], queryFn: listExercices, enabled: perms.canConsulter });
  const { data: detail } = useQuery({ queryKey: ['liasse-detail', selId], queryFn: () => getExercice(selId), enabled: !!selId });

  useEffect(() => {
    if (exercices && exercices.length && !selId) setSelId(exercices[0].id);
  }, [exercices, selId]);

  // Initialise les champs éditables depuis les valeurs de l'exercice.
  useEffect(() => {
    if (!detail) return;
    const e: Edits = {};
    for (const [t, lignes] of Object.entries(detail.valeurs)) {
      e[t] = {};
      for (const [cle, cols] of Object.entries(lignes)) {
        e[t][cle] = {};
        for (const [col, v] of Object.entries(cols)) e[t][cle][col] = String(v ?? '');
      }
    }
    setEdits(e);
  }, [detail]);

  const seedMut = useMutation({
    mutationFn: seedLiasse2023,
    onSuccess: (ex) => { qc.invalidateQueries({ queryKey: ['liasse-exercices'] }); setSelId(ex.id); toast.success('Liasse 2023 chargée.'); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });
  const dupMut = useMutation({
    mutationFn: () => dupliquerExercice(selId),
    onSuccess: (ex) => { qc.invalidateQueries({ queryKey: ['liasse-exercices'] }); setSelId(ex.id); toast.success(`Exercice ${ex.annee} créé (comparatif pré-rempli).`); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });
  const genMut = useMutation({
    mutationFn: () => genererDepuisBalance(selId),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['liasse-detail', selId] }); toast.success(r.detail); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });
  const saveMut = useMutation({
    mutationFn: (t: TableauTemplate) => {
      const payload: Record<string, Record<string, number | null>> = {};
      for (const l of t.lignes) {
        if (!l.cle) continue;
        payload[l.cle] = {};
        for (const col of t.colonnes.slice(1)) {
          const raw = edits[t.tableau]?.[l.cle]?.[col] ?? '';
          payload[l.cle][col] = raw === '' ? null : Number(raw);
        }
      }
      return setValeurs(selId, t.tableau, payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['liasse-detail', selId] }); toast.success('Valeurs enregistrées.'); },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const tabKeys = useMemo(() => (templates ? Object.keys(templates) : []), [templates]);

  if (!perms.canConsulter) {
    return <AccesRefuse detail="La liasse fiscale requiert la permission « Liasse fiscale ». Contactez votre administrateur." />;
  }

  const ex = detail?.exercice;
  const tpl = templates?.[tab];

  return (
    <>
      <PageHeader
        title="Liasse fiscale (imprimé administration)"
        subtitle="Bilan Actif / Passif + Compte de résultat — fidèle au déposé, export PDF & Excel"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Comptabilité' }, { label: 'Liasse fiscale' }]}
        actions={
          <div className="flex flex-wrap gap-2">
            {(!exercices || exercices.length === 0) && perms.canModifier && (
              <Button variant="secondary" onClick={() => seedMut.mutate()} loading={seedMut.isPending}>
                <Sparkles /> Charger 2023
              </Button>
            )}
            {selId && (
              <>
                <Button variant="secondary" onClick={() => ex && downloadLiasse(selId, 'pdf', ex.annee)}><FileText /> PDF</Button>
                <Button variant="secondary" onClick={() => ex && downloadLiasse(selId, 'excel', ex.annee)}><FileSpreadsheet /> Excel</Button>
                {perms.canModifier && (
                  <Button
                    variant="secondary"
                    onClick={() => { if (window.confirm("Générer les valeurs de cet exercice depuis la balance comptable ? Les colonnes de l'exercice sont remplacées ; le comparatif N-1 est conservé. À valider ensuite.")) genMut.mutate(); }}
                    loading={genMut.isPending}
                  >
                    <RefreshCw /> Depuis la balance
                  </Button>
                )}
                {perms.canModifier && <Button variant="secondary" onClick={() => dupMut.mutate()} loading={dupMut.isPending}><Copy /> Dupliquer N-1</Button>}
              </>
            )}
          </div>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !exercices || exercices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">
          Aucun exercice de liasse. {perms.canModifier ? 'Cliquez « Charger 2023 » pour importer la liasse déposée.' : ''}
        </div>
      ) : (
        <div className="space-y-5">
          {/* Sélecteur d'exercice */}
          <div className="flex flex-wrap items-center gap-2">
            {exercices.map((x) => (
              <button
                key={x.id}
                onClick={() => setSelId(x.id)}
                className={cn('rounded-md border px-3 py-1.5 text-sm font-semibold',
                  selId === x.id ? 'border-brand-primary bg-brand-primary text-white' : 'border-border bg-surface-secondary hover:bg-surface-hover')}
              >
                {x.annee} <span className="ml-1 text-xs opacity-80">{x.statut}</span>
              </button>
            ))}
          </div>

          {ex && (
            <div className="rounded-lg border border-border bg-surface-secondary p-4 text-sm shadow-sm">
              <p className="font-bold text-content-primary">{ex.designation}</p>
              <p className="text-content-secondary">N.I.F : {ex.nif} · {ex.activite}</p>
              <p className="text-content-muted">{ex.adresse} · Exercice clos le {ex.date_cloture}</p>
            </div>
          )}

          {/* Contrôles d'écarts (tolérance 5 DA) */}
          {detail && detail.controles.length > 0 && (
            <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <p className="mb-2 label-caps">Contrôles de cohérence (tolérance 5 DA)</p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {detail.controles.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-content-secondary">{c.libelle}</span>
                    <span className={cn('rounded px-2 py-0.5 text-xs font-semibold',
                      c.ok ? 'bg-status-done/10 text-status-done' : 'bg-status-stuck/10 text-status-stuck')}>
                      {c.ok ? 'OK' : 'Écart'} {c.ecart !== 0 && `(${formatDZD(c.ecart, { decimals: 0 })})`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Éditeur par tableau */}
          <Tabs
            tabs={tabKeys.map((k) => ({ key: k, label: templates![k].titre }))}
            value={tab}
            onChange={setTab}
            className="mb-4"
          />
          {tpl && (
            <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-surface-primary">
                  <tr className="border-b border-border text-left">
                    <th className="px-3 py-2 label-caps">{tpl.entetes[0]}</th>
                    {tpl.colonnes.slice(1).map((col, i) => (
                      <th key={col} className="px-3 py-2 text-right label-caps">
                        {tpl.entetes[i + 1]} {ex ? (tpl.col_annee[col] === 'n' ? ex.annee : ex.annee - 1) : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tpl.lignes.map((l, idx) => {
                    if (l.type === 'section') {
                      return (
                        <tr key={idx} className="bg-surface-primary/70">
                          <td colSpan={tpl.colonnes.length} className="px-3 py-1.5 font-bold text-content-primary">{l.libelle}</td>
                        </tr>
                      );
                    }
                    const bold = l.type === 'total';
                    return (
                      <tr key={idx} className={cn('border-b border-border last:border-0', bold && 'bg-surface-primary/40')}>
                        <td className={cn('px-3 py-1', l.type === 'sub' && 'pl-8', bold && 'font-bold')}>{l.libelle}</td>
                        {tpl.colonnes.slice(1).map((col) => (
                          <td key={col} className="px-1 py-0.5">
                            {l.cle && perms.canModifier ? (
                              <Input
                                type="number"
                                step="1"
                                className="h-8 text-right tabular-nums"
                                value={edits[tpl.tableau]?.[l.cle]?.[col] ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setEdits((m) => {
                                    const t = { ...(m[tpl.tableau] ?? {}) };
                                    const row = { ...(t[l.cle!] ?? {}) };
                                    row[col] = v;
                                    t[l.cle!] = row;
                                    return { ...m, [tpl.tableau]: t };
                                  });
                                }}
                              />
                            ) : l.cle ? (
                              <div className="px-2 text-right tabular-nums">{formatDZD(Number(edits[tpl.tableau]?.[l.cle]?.[col] || 0), { decimals: 0 }) || ''}</div>
                            ) : null}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {perms.canModifier && (
                <div className="flex items-center justify-between gap-2 border-t border-border bg-surface-primary/40 px-3 py-2">
                  <span className="text-xs text-content-muted">Saisissez les montants tels que déposés ; les totaux ne sont pas recalculés.</span>
                  <Button size="sm" onClick={() => saveMut.mutate(tpl)} loading={saveMut.isPending}><Save /> Enregistrer {tpl.titre}</Button>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-content-muted">
            <Download className="size-3.5" /> Le PDF reproduit l'imprimé officiel (bandeau, NIF en cases, NÉANT). L'Excel réplique les onglets.
            {ex && <StatusBadge status="info" label={`Statut : ${ex.statut}`} />}
          </div>
        </div>
      )}
    </>
  );
}
